import type { MetaFunction } from '@remix-run/node';

export const meta: MetaFunction = () => {
  return [
    { title: 'Terms of Service - MatrixFit Recommender' },
    { name: 'description', content: 'Terms of Service for MatrixFit Recommender by AppScout Labs' },
  ];
};

export default function TermsOfService() {
  return (
    <div style={{ maxWidth: '800px', margin: '0 auto', padding: '40px 20px', fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', color: '#1a1a1a', lineHeight: 1.7 }}>
      <h1 style={{ fontSize: '2rem', marginBottom: '8px' }}>Terms of Service for MatrixFit Recommender</h1>
      <p style={{ color: '#666', marginBottom: '32px' }}>
        <strong>Effective Date:</strong> February 15, 2026 | <strong>Last Updated:</strong> February 15, 2026
      </p>

      <Section title="1. Acceptance of Terms">
        <p>
          Welcome to MatrixFit Recommender ("the App," "we," "our," or "us"). These Terms of Service ("Terms") govern your access to and use of MatrixFit Recommender, a Shopify application that provides size recommendation functionality for apparel merchants.
        </p>
        <p>
          By installing, accessing, or using MatrixFit Recommender, you ("Merchant," "you," or "your") agree to be bound by these Terms. If you do not agree to these Terms, do not install or use the App.
        </p>
      </Section>

      <Section title="2. Definitions">
        <ul>
          <li><strong>"App"</strong> refers to MatrixFit Recommender, including all features, updates, and documentation</li>
          <li><strong>"Merchant"</strong> refers to the Shopify store owner who installs and uses the App</li>
          <li><strong>"Shopper"</strong> refers to end-users who visit the Merchant's store and use the size finder widget</li>
          <li><strong>"Size Matrix"</strong> refers to the CSV data uploaded by Merchants to generate size recommendations</li>
        </ul>
      </Section>

      <Section title="3. Eligibility">
        <p>To use MatrixFit Recommender, you must be at least 18 years old, have a valid Shopify store account, be authorized to bind your business to these Terms, and not be prohibited from using the App under applicable laws.</p>
      </Section>

      <Section title="4. License and Access">
        <p>Subject to your compliance with these Terms, we grant you a limited, non-exclusive, non-transferable, revocable license to access and use the App for your business purposes.</p>
        <p>You may NOT: reverse engineer, decompile, or disassemble the App; copy, modify, or create derivative works; rent, lease, sell, or sublicense the App; use the App to violate any laws; or interfere with the App's functionality.</p>
        <p>Your use is also governed by Shopify's Terms of Service, API Terms of Service, and Acceptable Use Policy.</p>
      </Section>

      <Section title="5. Account Responsibilities">
        <p>You are responsible for maintaining the security of your Shopify account, all activity under your account, the accuracy of size matrices you upload, and complying with applicable consumer protection laws.</p>
      </Section>

      <Section title="6. Fees and Payment">
        <p>MatrixFit Recommender offers multiple pricing tiers:</p>
        <ul>
          <li><strong>Free Tier:</strong> Limited features (1 matrix, basic functionality)</li>
          <li><strong>Pro Tier:</strong> $4.99/month (unlimited matrices, analytics)</li>
          <li><strong>Plus Tier:</strong> $9.99/month (ROI tracking, priority support)</li>
          <li><strong>Enterprise Tier:</strong> $19.99/month (white-label, API access, dedicated support)</li>
        </ul>
        <p>Fees are billed monthly through Shopify's billing system in USD. No refunds for partial months; cancel anytime. We reserve the right to change pricing with 30 days' advance notice.</p>
      </Section>

      <Section title="7. Service Availability">
        <p>We strive to maintain 99.5% uptime but do NOT guarantee uninterrupted service. Support tiers range from documentation-only (Free) to dedicated support with 12-hour response time (Enterprise).</p>
      </Section>

      <Section title="8. Your Content and Data">
        <p>You retain all rights to your size matrices and product data. You grant us a limited license to store and process your data to provide the Services. You represent that you own or have the right to upload all size matrices and that they comply with applicable laws.</p>
        <p><strong>We are NOT responsible for the accuracy of size matrices you upload or incorrect size recommendations resulting from inaccurate matrices.</strong></p>
      </Section>

      <Section title="9. Privacy and Data Protection">
        <p>Our collection and use of data is governed by our <a href="/privacy">Privacy Policy</a>. When shoppers use the size finder widget, you are the Data Controller (under GDPR) and we are the Data Processor.</p>
      </Section>

      <Section title="10. Intellectual Property">
        <p>MatrixFit Recommender and all related technology, code, designs, and trademarks are owned by us or our licensors. These Terms do NOT grant you any ownership rights.</p>
      </Section>

      <Section title="11. Termination">
        <p>You may terminate at any time by uninstalling the App. We may suspend or terminate your access if you violate these Terms. Upon termination, your data will be deleted within 30 days unless required by law to retain.</p>
      </Section>

      <Section title="12. Disclaimers">
        <p>THE APP IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND. We do NOT guarantee that the App will reduce your return rates, increase your sales, or provide perfectly accurate size recommendations.</p>
      </Section>

      <Section title="13. Limitation of Liability">
        <p>TO THE FULLEST EXTENT PERMITTED BY LAW, our total liability shall not exceed the greater of $100 USD or the amount you paid us in the 12 months preceding the claim. We are not liable for indirect, incidental, special, consequential, or punitive damages.</p>
      </Section>

      <Section title="14. Indemnification">
        <p>You agree to indemnify and hold harmless MatrixFit Recommender from any claims arising from your use of the App, violation of these Terms, or your size matrices and product data.</p>
      </Section>

      <Section title="15. Dispute Resolution">
        <p>These Terms are governed by the laws of the jurisdiction where AppScout Labs is registered. Disputes shall be resolved through binding arbitration, except for small claims court disputes or injunctive relief for IP infringement. You agree to resolve disputes individually, NOT as part of a class action.</p>
      </Section>

      <Section title="16. Miscellaneous">
        <p>These Terms and our Privacy Policy constitute the entire agreement. We may modify these Terms with 30 days' notice for material changes. Our failure to enforce any provision does not waive our right to enforce it later. If any provision is invalid, the remaining provisions remain in effect.</p>
      </Section>

      <Section title="17. Contact Information">
        <p>For questions about these Terms, contact us at:</p>
        <p>
          <strong>MatrixFit Recommender Support</strong><br />
          Email: <a href="mailto:support@labs.appscout.io">support@labs.appscout.io</a><br />
          Website: <a href="https://matrixfit.labs.appscout.io">https://matrixfit.labs.appscout.io</a><br />
          Company: AppScout Labs
        </p>
      </Section>

      <hr style={{ margin: '32px 0', border: 'none', borderTop: '1px solid #e0e0e0' }} />
      <p style={{ color: '#666', fontSize: '0.9rem' }}>
        By installing MatrixFit Recommender, you acknowledge that you have read, understood, and agree to these Terms of Service.
      </p>
      <p style={{ color: '#666', fontSize: '0.9rem' }}>
        Last Reviewed: February 15, 2026
      </p>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ marginBottom: '32px' }}>
      <h2 style={{ fontSize: '1.4rem', marginBottom: '12px', borderBottom: '1px solid #e0e0e0', paddingBottom: '8px' }}>{title}</h2>
      {children}
    </section>
  );
}
